import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
  LoadingEvent,
  useSnackbar,
  useUserAccess,
} from '@ui-framework/index';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowTab,
  ArrowTabGroup,
  CustomTabPanel,
  FormFields,
  IconLibrary,
  Layout,
  StackablePageButtons,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import NotificationPreferencesForm from '@product-framework/pages/Notifications/components/NotificationPreferencesForm';
import {
  MY_NOTIFICATION_PREFERENCES_API_PATH,
  buildUserNotificationPreferencesApiPath,
} from '@product-framework/pages/Notifications/apiPaths';
import {
  canManageUserNotificationPreferences,
  getCurrentUserId,
  isSameUserId,
} from '@product-framework/pages/Notifications/components/utils';
import {
  UserResetMFADialogWithButton,
  UserSendPasswordResetEmailDialogWithButton,
  UserUpdatePasswordDialogWithButton,
} from '@product-framework/components/Dialogs';
import { editSASchema, editAdminSchema } from '../../schemas';
import validationSchema from './validations';
import { USER_EDIT_TABS, getUserEditTabIndex } from './tabs';

export { USER_EDIT_TABS, getUserEditTabIndex } from './tabs';

function UserEdit() {
  const { formatMessage } = useLocalization();
  const editFormRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const { id: pageId, tab } = useParams();
  const userAccess = useUserAccess();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, getApiUrl, get } = useDataProvider(provider);

  const updateApiUrl = `${getApiUrl()}/sxcl/v1/users/${pageId}`;
  const getPageDataApiUrl = `${getApiUrl()}/sxcl/v1/users/${pageId}`;

  const [initialValues, setInitialValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [formErrors, setFormErrors] = useState(null);
  const isOwnProfile = isSameUserId(pageId, getCurrentUserId(userAccess));
  const canViewPreferences =
    isOwnProfile ||
    (isSameUserId(initialValues.id, pageId) &&
      canManageUserNotificationPreferences(
        userAccess,
        initialValues.role?.companyType,
      ));
  const requestedTabIndex = getUserEditTabIndex(tab);
  const tabValue =
    requestedTabIndex === USER_EDIT_TABS.NOTIFICATIONS.index &&
    canViewPreferences
      ? USER_EDIT_TABS.NOTIFICATIONS.index
      : USER_EDIT_TABS.DETAILS.index;
  const preferencesApiPath = isOwnProfile
    ? MY_NOTIFICATION_PREFERENCES_API_PATH
    : buildUserNotificationPreferencesApiPath(pageId);
  const preProcessRequestData = data => {
    const { enabled, companyId, smsSubscriptionStatus, id, role, ...other } =
      data;
    return {
      enabled: enabled === 'true',
      userRoleId: role?.value,
      ...other,
    };
  };

  const preProcessFetchData = data => {
    const { role, ...other } = data;
    return {
      ...other,
      role: {
        label: role?.roleIdentifier,
        value: role?.id,
        companyType: role?.companyType,
      },
    };
  };
  const getSchema = () => {
    if (userAccess.isAdmin()) {
      return editAdminSchema;
    }
    return editSASchema;
  };

  const submitEditForm = async formData => {
    const { disabledReason, ...data } = formData;
    setFormErrors(null);
    const requestData = preProcessRequestData(data);
    const response = await put(updateApiUrl, { ...requestData });
    if (response?.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'USER_WAS_SAVED_SUCCESSFULLY',
          defaultMessage: 'User was saved successfully',
        }),
      );
      setInitialValues(preProcessFetchData(response?.data));
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'ERROR_COULD_NOT_SAVE_THE_USER',
          defaultMessage: 'Error: Could not save the user',
        }),
      });
    }
  };

  const fetchInitialData = async () => {
    setFormErrors(null);
    const response = await get(getPageDataApiUrl);
    if (response?.ok) {
      setInitialValues({
        ...initialValues,
        ...preProcessFetchData(response?.data),
      });
      setLoading(false);
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'ERROR_COULD_NOT_FETCH_THE_USER_DATA',
          defaultMessage: 'Error: Could not fetch the user data',
        }),
      });
    }
  };

  useEffect(() => {
    fetchInitialData(pageId);
  }, [pageId]);

  const formProps = {
    customComponents: FormFields,
    schema: getSchema(),
    onSubmit: submitEditForm,
    ref: editFormRef,
    initialValues,
    validationSchema: validationSchema(formatMessage, getApiUrl, get),
    permissionEntity: userAccess.isAdmin()
      ? 'ADMINUSER'
      : 'SERVICEADMINISTRATORUSER',
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'USERS',
        defaultMessage: 'Users',
      }),
      icon: IconLibrary.Users,
      onClick: () => {
        navigate('/users');
      },
    },
    {
      name: formatMessage({
        id: 'EDIT_USER',
        defaultMessage: 'Edit User',
      }),
      icon: IconLibrary.User,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'CREATE_USER',
        defaultMessage: 'Create User',
      }),
      Icon: IconLibrary.User,
      permissionName: 'CAN_CREATE_USER',
      variant: 'outlined',
      onClick: () => navigate('/users/create'),
    },
    {
      label: formatMessage({
        id: 'LIST_USERS',
        defaultMessage: 'List Users',
      }),
      Icon: IconLibrary.Users,
      variant: 'outlined',
      onClick: () => navigate('/users'),
    },
  ];

  return (
    <Layout>
      {loading && (
        <LoadingEvent mt={4}>
          <FormattedMessage id="PROCESSING" defaultMessage="Processing" />
          ...
        </LoadingEvent>
      )}

      {!loading && (
        <Grid2 xs={12} container>
          <Grid2 xs={12} md={6}>
            <Breadcrumb items={breadCrumb} />
          </Grid2>
          <Grid2 xs={12} md={6}>
            <StackablePageButtons buttons={stackablePageButtons} />
          </Grid2>
        </Grid2>
      )}

      {!loading && canViewPreferences && (
        <ArrowTabGroup
          value={tabValue}
          onChange={(event, newValue) => {
            const selectedTab = Object.values(USER_EDIT_TABS).find(
              ({ index }) => index === newValue,
            );
            navigate(`/users/edit/${pageId}/${selectedTab.name}`, {
              replace: true,
            });
          }}>
          <ArrowTab
            value={USER_EDIT_TABS.DETAILS.index}
            maxTabs={2}
            arrowHeadArea={94}
            label={formatMessage({
              id: 'USER_DETAILS',
              defaultMessage: 'User Details',
            })}
          />
          <ArrowTab
            value={USER_EDIT_TABS.NOTIFICATIONS.index}
            maxTabs={2}
            label={formatMessage({
              id: 'NOTIFICATIONS',
              defaultMessage: 'Notifications',
            })}
          />
        </ArrowTabGroup>
      )}

      {!loading && (
        <CustomTabPanel
          value={tabValue}
          index={USER_EDIT_TABS.DETAILS.index}
          keepMounted>
          <PageSection
            label={formatMessage({
              id: 'EDIT_USER',
              defaultMessage: 'Edit User',
            })}>
            <Box>
              <CRUDComponent
                errors={formErrors}
                type={CRUD_COMPONENT_TYPE.FORM}
                formProps={formProps}>
                <Box
                  display="flex"
                  justifyContent="center"
                  gap={2}
                  mt={3}
                  mb={1}>
                  <PermissionButton
                    permissionName="CAN_CREATE_EDIT_USER"
                    variant="contained"
                    startIcon={<IconLibrary.Save />}
                    color="primary"
                    onClick={() => editFormRef.current?.submit()}>
                    {formatMessage({
                      id: 'SAVE_USER',
                      defaultMessage: 'Save User',
                    })}
                  </PermissionButton>
                  <PermissionButton
                    permissionName="CAN_CREATE_EDIT_USER"
                    variant="contained"
                    color="primary"
                    startIcon={<IconLibrary.Reset />}
                    onClick={() => editFormRef.current?.reset()}>
                    {formatMessage({
                      id: 'RESET',
                      defaultMessage: 'Reset',
                    })}
                  </PermissionButton>
                  <UserUpdatePasswordDialogWithButton
                    email={initialValues?.email}
                    firstName={initialValues?.firstName}
                    lastName={initialValues?.lastName}
                    permissionName="CAN_UPDATE_SA_PASSWORD"
                  />
                  <UserResetMFADialogWithButton
                    email={initialValues?.email}
                    firstName={initialValues?.firstName}
                    lastName={initialValues?.lastName}
                    permissionName="CAN_RESET_SA_USER_MFA"
                  />
                  <UserSendPasswordResetEmailDialogWithButton
                    email={initialValues?.email}
                    firstName={initialValues?.firstName}
                    lastName={initialValues?.lastName}
                    permissionName="CAN_SEND_SA_RESET_PASSWORD_EMAIL"
                  />
                </Box>
              </CRUDComponent>
            </Box>
          </PageSection>
        </CustomTabPanel>
      )}

      {!loading && canViewPreferences && (
        <CustomTabPanel
          value={tabValue}
          index={USER_EDIT_TABS.NOTIFICATIONS.index}>
          <PageSection
            label={formatMessage({
              id: 'NOTIFICATION_PREFERENCES',
              defaultMessage: 'Notification Preferences',
            })}>
            <NotificationPreferencesForm apiPath={preferencesApiPath} />
          </PageSection>
        </CustomTabPanel>
      )}
    </Layout>
  );
}

export default observer(UserEdit);
